import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useSearchParams } from 'react-router-dom';
import { Drawer, Spin, Button, Result } from 'antd';
import { Sparkles } from 'lucide-react';
import { useMount, useDebounceFn } from 'ahooks';
import { extensions } from './extensions';
import { EditorTopBar } from './components/EditorTopBar';
import { EditorOutline } from './components/EditorOutline';
import { EditorRightPanel } from './components/EditorRightPanel';
import { ChapterProcessingOverlay } from './components/ChapterProcessingOverlay';
import { EditorContextProvider, useEditorContext } from './contexts/EditorContext';
import { useBreakpoint } from './hooks/useBreakpoint';
import { get, post } from '../../axios/axios';
import { convertBlocksToTiptapContent, extractBlockContentByBlockId } from './utils/blockConverter';
import {
    extractSelectedContent,
    getHeadingInfo,
    isSelectionInProtectedContent,
} from './utils/selectionUtils';
import { PANEL_MODE, PANEL_TAB } from './components/constants';
import styles from './styles/index.module.scss';
import './styles/index.scss';
import 'katex/dist/katex.min.css';

const BLOCKS_API = '/ai/ai/paper/editor/v2/blocks';
const SAVE_API = '/ai/ai/paper/editor/v2/save';

/**
 * 开发环境的本地数据钩子：后端不可达时，可以在控制台注入
 *   window.__LGFB_EDITOR_MOCK__ = { paperTitle, blocks }
 * 或 sessionStorage.setItem('__LGFB_EDITOR_MOCK__', JSON.stringify({...})) 后刷新。
 */
const readDevMock = () => {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return null;
    try {
        const raw =
            window.__LGFB_EDITOR_MOCK__ || window.sessionStorage.getItem('__LGFB_EDITOR_MOCK__');
        if (!raw) return null;
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (error) {
        return null;
    }
};

/**
 * 论文在线编辑器（V2）
 */
const EditorV2 = () => {
    return (
        <EditorContextProvider>
            <EditorV2Inner />
        </EditorContextProvider>
    );
};

const EditorV2Inner = () => {
    const [searchParams] = useSearchParams();
    const { isMobile, isCompact } = useBreakpoint();
    const {
        setEditor,
        uiState,
        selectionState,
        documentState,
        saveState,
        scrollContainerRef,
        openToolPanel,
        registerDocumentReloader,
    } = useEditorContext();

    // 脏块 / 已删除块追踪
    const dirtyBlockIdsRef = useRef(new Set());
    const deletedBlockIdsRef = useRef(new Set());
    const isSettingContentRef = useRef(false);
    const isSavingRef = useRef(false);
    const [reloadKey, setReloadKey] = useState(0);

    const orderNo = searchParams.get('orderNo');
    useEffect(() => {
        documentState.orderNo = orderNo;
    }, [orderNo, documentState]);

    // ========== 加载文档 ==========

    const loadDocument = useCallback(async () => {
        const no = searchParams.get('orderNo');
        uiState.loading = true;
        uiState.loadError = '';
        if (!no) {
            uiState.loading = false;
            uiState.loadError = '缺少订单号，请从订单页面点击「在线编辑」进入';
            return;
        }
        try {
            const mock = readDevMock();
            // slient：加载失败时由页面自己提示，不弹全局错误
            const data = mock || (await get(BLOCKS_API, { orderNo: no }, { slient: true }))?.data;
            const blocks = data?.blocks || [];
            documentState.editorContent = convertBlocksToTiptapContent(blocks);
            uiState.paperTitle = data?.paperTitle || '';
            uiState.loading = false;
        } catch (error) {
            console.error('Failed to load editor data:', error);
            uiState.loading = false;
            uiState.loadError =
                error?.response?.data?.msg || '论文内容加载失败，请检查订单是否已生成完成';
        }
    }, [searchParams, uiState, documentState]);

    useMount(loadDocument);

    useEffect(() => {
        registerDocumentReloader(loadDocument);
    }, [registerDocumentReloader, loadDocument]);

    useEffect(() => {
        if (reloadKey > 0) loadDocument();
    }, [reloadKey, loadDocument]);

    // ========== 自动保存 ==========

    const saveEditorContent = useCallback(
        async (editor) => {
            if (!editor) return;
            const blockIdsToSave = [...dirtyBlockIdsRef.current];
            const deletedIds = [...deletedBlockIdsRef.current];
            if (blockIdsToSave.length === 0 && deletedIds.length === 0) return;

            dirtyBlockIdsRef.current.clear();
            deletedBlockIdsRef.current.clear();
            isSavingRef.current = true;
            saveState.status = 'saving';

            const saveTasks = [];
            blockIdsToSave.forEach((blockId) => {
                if (deletedIds.includes(blockId)) return;
                saveTasks.push({ blockId, isDelete: false });
            });
            deletedIds.forEach((blockId) => saveTasks.push({ blockId, isDelete: true }));

            try {
                const results = await Promise.allSettled(
                    saveTasks.map(async ({ blockId, isDelete }) => {
                        const editedContent = isDelete
                            ? ''
                            : (extractBlockContentByBlockId(editor, blockId) ?? '');
                        await post(SAVE_API, { contentBlockId: blockId, editedContent });
                    }),
                );

                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        const task = saveTasks[index];
                        if (task.isDelete) deletedBlockIdsRef.current.add(task.blockId);
                        else dirtyBlockIdsRef.current.add(task.blockId);
                        console.error(`保存块 ${task.blockId} 失败:`, result.reason);
                    }
                });

                if (results.some((r) => r.status === 'fulfilled')) {
                    saveState.status = 'saved';
                    saveState.lastSavedTime = Date.now();
                } else {
                    saveState.status = 'idle';
                }
            } catch (error) {
                console.error('自动保存失败:', error);
                blockIdsToSave.forEach((id) => dirtyBlockIdsRef.current.add(id));
                deletedIds.forEach((id) => deletedBlockIdsRef.current.add(id));
                saveState.status = 'idle';
            } finally {
                isSavingRef.current = false;
            }
        },
        [saveState],
    );

    // 防抖保存（1.5 秒，最长 5 秒强制触发一次）
    const { run: debouncedSave, flush: flushSave } = useDebounceFn(saveEditorContent, {
        wait: 1500,
        maxWait: 5000,
    });

    // ========== 编辑器 ==========

    const editor = useEditor({
        extensions,
        content: documentState.editorContent,
        onSelectionUpdate: ({ editor: instance }) => {
            const { from, to } = instance.state.selection;
            const headingInfo = getHeadingInfo(instance);

            const clearTextSelection = () => {
                selectionState.selectedText = '';
                selectionState.selectedBlockIds = [];
                selectionState.selectedRange = { from: null, to: null };
            };

            if (headingInfo.isHeading) {
                clearTextSelection();
                if (headingInfo.node.attrs?.isProtected === true) {
                    selectionState.isHeadingSelected = false;
                    selectionState.selectedHeadingInfo = null;
                } else {
                    selectionState.isHeadingSelected = true;
                    selectionState.selectedHeadingInfo = {
                        node: headingInfo.node,
                        pos: headingInfo.pos,
                        level: headingInfo.level,
                        detailId: headingInfo.detailId,
                        blockId: headingInfo.blockId,
                    };
                }
                selectionState.version += 1;
                return;
            }

            selectionState.isHeadingSelected = false;
            selectionState.selectedHeadingInfo = null;

            if (isSelectionInProtectedContent(instance)) {
                clearTextSelection();
                selectionState.version += 1;
                return;
            }

            const result = extractSelectedContent(instance);
            selectionState.selectedText = result.content;
            selectionState.selectedBlockIds = result.blockIds;
            selectionState.selectedRange = { from, to };
            selectionState.version += 1;
        },
        onUpdate: ({ editor: instance, transaction }) => {
            if (isSettingContentRef.current) return;
            if (transaction.getMeta('skipDirtyTracking')) return;

            const { from } = instance.state.selection;
            const { doc } = instance.state;
            doc.nodesBetween(from, from, (node) => {
                if (node.attrs && node.attrs.blockId) {
                    dirtyBlockIdsRef.current.add(node.attrs.blockId);
                    return false;
                }
                return true;
            });

            const prevDoc = transaction.before;
            if (prevDoc && prevDoc !== doc) {
                const prevBlockIds = new Set();
                prevDoc.descendants((node) => {
                    if (node.attrs?.blockId) prevBlockIds.add(node.attrs.blockId);
                });
                const currentBlockIds = new Set();
                doc.descendants((node) => {
                    if (node.attrs?.blockId) currentBlockIds.add(node.attrs.blockId);
                });
                prevBlockIds.forEach((id) => {
                    if (!currentBlockIds.has(id)) {
                        deletedBlockIdsRef.current.add(id);
                        dirtyBlockIdsRef.current.delete(id);
                    }
                });
            }

            debouncedSave(instance);
        },
    });

    useEffect(() => {
        if (!editor) return;
        setEditor(editor);
        if (process.env.NODE_ENV === 'development') {
            // 开发调试：控制台可通过 window.__LGFB_EDITOR__ 访问编辑器实例
            window.__LGFB_EDITOR__ = editor;
        }
    }, [editor, setEditor]);

    // 标题旁的「改稿」按钮：选中该标题并打开章节改稿
    useEffect(() => {
        if (!editor) return undefined;
        const editorElement = editor.view.dom;
        const handleChapterRewrite = (e) => {
            const { pos } = e.detail || {};
            if (typeof pos === 'number') {
                editor.commands.setNodeSelection(pos);
            }
            openToolPanel(PANEL_TAB.CHAPTER);
        };
        editorElement.addEventListener('tiptap-chapter-rewrite', handleChapterRewrite);
        return () => {
            editorElement.removeEventListener('tiptap-chapter-rewrite', handleChapterRewrite);
        };
    }, [editor, openToolPanel]);

    // 应用局部修改结果（替换选中文本）
    const handleApplyTextResult = useCallback(
        (result, type, range, blockId) => {
            if (!editor || !result) return;

            if (range && range.from !== null && range.to !== null) {
                const startPos = range.from;
                editor
                    .chain()
                    .focus()
                    .setTextSelection({ from: range.from, to: range.to })
                    .deleteSelection()
                    .insertContent(result)
                    .run();
                const endPos = editor.state.selection.$anchor.pos;
                editor.chain().setTextSelection({ from: startPos, to: endPos }).run();
            } else {
                const currentPos = editor.state.selection.from;
                editor.chain().focus().insertContent(result).run();
                const endPos = editor.state.selection.$anchor.pos;
                editor.chain().setTextSelection({ from: currentPos, to: endPos }).run();
            }

            if (blockId) dirtyBlockIdsRef.current.add(blockId);
            const finalFrom = editor.state.selection.from;
            editor.state.doc.nodesBetween(finalFrom, finalFrom, (node) => {
                if (node.attrs?.blockId) {
                    dirtyBlockIdsRef.current.add(node.attrs.blockId);
                    return false;
                }
                return true;
            });
        },
        [editor],
    );

    // 离开页面 / 切后台时立即保存
    useEffect(() => {
        const hasPendingChanges = () =>
            dirtyBlockIdsRef.current.size > 0 ||
            deletedBlockIdsRef.current.size > 0 ||
            isSavingRef.current;

        const handleBeforeUnload = (e) => {
            if (!hasPendingChanges()) return;
            flushSave();
            e.preventDefault();
            e.returnValue = '';
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && hasPendingChanges()) flushSave();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            flushSave();
        };
    }, [flushSave]);

    // 数据加载完成后写入编辑器
    useEffect(() => {
        if (editor && documentState.editorContent) {
            isSettingContentRef.current = true;
            editor.commands.setContent(documentState.editorContent);
            requestAnimationFrame(() => {
                isSettingContentRef.current = false;
            });
        }
    }, [editor, documentState.editorContent]);

    // 窄屏：从桌面布局切过来时关掉抽屉
    useEffect(() => {
        if (!isMobile) uiState.toolsOpen = false;
        if (!isCompact) uiState.outlineOpen = false;
    }, [isMobile, isCompact, uiState]);

    if (!editor) return null;

    const showInlineOutline = !isCompact && !uiState.outlineCollapsed;
    const hasSelectionTarget =
        uiState.panelMode !== PANEL_MODE.EMPTY && uiState.panelMode !== PANEL_MODE.REWRITE_CHAPTER;

    const renderPaper = () => {
        if (uiState.loading) {
            return (
                <div className={styles.paperLoading}>
                    <Spin size="large" />
                    <div>正在载入论文内容…</div>
                </div>
            );
        }
        if (uiState.loadError) {
            return (
                <Result
                    status="warning"
                    title="暂时打不开这篇论文"
                    subTitle={uiState.loadError}
                    extra={
                        <Button type="primary" onClick={() => setReloadKey((k) => k + 1)}>
                            重新加载
                        </Button>
                    }
                />
            );
        }
        return (
            <>
                <ChapterProcessingOverlay />
                {uiState.paperTitle && <h1 className={styles.paperTitle}>{uiState.paperTitle}</h1>}
                <EditorContent editor={editor} />
            </>
        );
    };

    return (
        <div className={`${styles.shell} ${isMobile ? styles.shellMobile : ''}`}>
            <EditorTopBar isMobile={isMobile} isCompact={isCompact} />

            <div className={styles.workspace}>
                {showInlineOutline && (
                    <aside className={styles.outlinePane}>
                        <EditorOutline />
                    </aside>
                )}

                <main className={styles.paperScroll} ref={scrollContainerRef}>
                    <div className={styles.paperStage}>
                        <div className={styles.paper}>{renderPaper()}</div>
                    </div>
                </main>

                {!isMobile && (
                    <aside className={styles.toolPane}>
                        <EditorRightPanel onApplyTextResult={handleApplyTextResult} />
                    </aside>
                )}
            </div>

            {isCompact && (
                <Drawer
                    placement="left"
                    open={uiState.outlineOpen}
                    onClose={() => {
                        uiState.outlineOpen = false;
                    }}
                    width={isMobile ? '82vw' : 300}
                    closable={false}
                    className={styles.outlineDrawer}
                    styles={{ body: { padding: 0 } }}
                >
                    <EditorOutline
                        onNavigate={() => {
                            uiState.outlineOpen = false;
                        }}
                    />
                </Drawer>
            )}

            {isMobile && (
                <>
                    <Drawer
                        placement="bottom"
                        open={uiState.toolsOpen}
                        onClose={() => {
                            uiState.toolsOpen = false;
                        }}
                        height="78vh"
                        closable={false}
                        forceRender
                        className={styles.toolsDrawer}
                        styles={{ body: { padding: 0 } }}
                    >
                        <div className={styles.drawerHandle} />
                        <EditorRightPanel onApplyTextResult={handleApplyTextResult} />
                    </Drawer>
                    {!uiState.toolsOpen && hasSelectionTarget && !uiState.loading && (
                        <button
                            type="button"
                            className={styles.mobileHint}
                            onClick={() => openToolPanel(PANEL_TAB.SELECTION)}
                        >
                            <Sparkles size={14} />
                            修改选中内容
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

export default EditorV2;
